import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Typography,
  Row,
  Col,
  Button,
  Space,
  Input,
  Alert,
  Switch,
  Table,
  Spin,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useCalibrationRecord,
  useCalibrationSchedules,
  useCreateCalibrationRecord,
  useUpdateCalibrationRecord,
} from '../../hooks/useCalibration';
import api from '../../services/api.service';
import type { CalibrationReading, CalibrationRecord, CalibrationRecordInput } from '../../services/calibration.service';
import { TRACEABILITY_OPTIONS } from '../tmmde/tmmdeDisplay';

const { Title, Text } = Typography;
const { TextArea } = Input;

type FormValues = {
  calibration_schedule_id: string;
  calibration_date: Dayjs;
  result: CalibrationRecord['result'];
  calibrator_name?: string;
  calibration_provider?: string;
  supplier_id?: string | null;
  calibration_certificate_number?: string;
  traceability_basis?: CalibrationRecord['traceability_basis'];
  measurement_uncertainty?: string;
  environmental_conditions?: string;
  adjusted?: boolean;
  calibration_valid_until?: Dayjs | null;
  as_found_reading?: string;
  as_left_reading?: string;
  calibration_notes?: string;
  failure_reason?: string;
  corrective_action?: string;
};

type ReadingRow = CalibrationReading & { key: string };

let readingSeq = 0;
const blankReading = (): ReadingRow => ({ key: `r${++readingSeq}`, result: null });

function unwrapArray<T>(res: unknown): T[] {
  const ax = (res as { data?: { data?: T[] } })?.data;
  return Array.isArray(ax) ? ax : Array.isArray(ax?.data) ? (ax as { data: T[] }).data : [];
}

const CalibrationRecordFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<FormValues>();
  const createMutation = useCreateCalibrationRecord();
  const updateMutation = useUpdateCalibrationRecord();
  const { data: existing, isLoading: loadingExisting } = useCalibrationRecord(id || '');
  const { data: schedules = [], isLoading: schedulesLoading } = useCalibrationSchedules();
  const [readings, setReadings] = useState<ReadingRow[]>([blankReading()]);
  const result = Form.useWatch('result', form);
  const scheduleId = Form.useWatch('calibration_schedule_id', form);

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-calibration'],
    queryFn: async () => {
      try {
        return unwrapArray<{ id: string; supplier_name: string; supplier_code?: string }>(
          await api.get('/procurement/suppliers')
        );
      } catch {
        return [];
      }
    },
    staleTime: 10 * 60 * 1000,
  });

  const scheduleOptions = useMemo(
    () =>
      schedules
        .filter((s) => s.equipment_id && (s.status !== 'Cancelled' || s.id === existing?.calibration_schedule_id))
        .map((s) => ({
          value: s.id,
          label: `${s.calibration_code} — ${[s.equipment_code, s.equipment_name].filter(Boolean).join(' ')}`.trim(),
        })),
    [schedules, existing?.calibration_schedule_id]
  );
  const selectedSchedule = schedules.find((s) => s.id === scheduleId);

  useEffect(() => {
    if (existing) {
      form.setFieldsValue({
        calibration_schedule_id: existing.calibration_schedule_id,
        calibration_date: dayjs(String(existing.calibration_date).slice(0, 10)),
        result: existing.result,
        calibrator_name: existing.calibrator_name,
        calibration_provider: existing.calibration_provider,
        supplier_id: existing.supplier_id || undefined,
        calibration_certificate_number: existing.calibration_certificate_number,
        traceability_basis: existing.traceability_basis || undefined,
        measurement_uncertainty: existing.measurement_uncertainty || undefined,
        environmental_conditions: existing.environmental_conditions || undefined,
        adjusted: Boolean(Number(existing.adjusted)),
        calibration_valid_until: existing.calibration_valid_until
          ? dayjs(String(existing.calibration_valid_until).slice(0, 10))
          : null,
        as_found_reading: existing.as_found_reading,
        as_left_reading: existing.as_left_reading,
        calibration_notes: existing.calibration_notes,
        failure_reason: existing.failure_reason,
        corrective_action: existing.corrective_action,
      });
      setReadings(
        existing.readings?.length
          ? existing.readings.map((r) => ({ ...r, key: r.id || `r${++readingSeq}` }))
          : [blankReading()]
      );
    } else if (!isEdit) {
      const sch = search.get('schedule');
      if (sch) form.setFieldValue('calibration_schedule_id', sch);
    }
  }, [existing, isEdit, search, form]);

  useEffect(() => {
    if (!isEdit && selectedSchedule?.supplier_id && !form.getFieldValue('supplier_id')) {
      form.setFieldValue('supplier_id', selectedSchedule.supplier_id);
    }
  }, [isEdit, selectedSchedule, form]);

  const setReading = (key: string, patch: Partial<CalibrationReading>) =>
    setReadings((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const onFinish = async (values: FormValues) => {
    const payload: CalibrationRecordInput = {
      calibration_date: values.calibration_date.format('YYYY-MM-DD'),
      result: values.result,
      calibrator_name: values.calibrator_name,
      calibration_provider: values.calibration_provider,
      supplier_id: values.supplier_id || null,
      calibration_certificate_number: values.calibration_certificate_number,
      traceability_basis: values.traceability_basis || null,
      measurement_uncertainty: values.measurement_uncertainty || null,
      environmental_conditions: values.environmental_conditions || null,
      adjusted: Boolean(values.adjusted),
      as_found_reading: values.as_found_reading,
      as_left_reading: values.as_left_reading,
      calibration_notes: values.calibration_notes,
      failure_reason: values.failure_reason,
      corrective_action: values.corrective_action,
      readings: readings.map(({ key: _k, id: _id, ...r }) => r),
    };
    if (values.calibration_valid_until) {
      payload.calibration_valid_until = values.calibration_valid_until.format('YYYY-MM-DD');
    }
    const saved =
      isEdit && id
        ? await updateMutation.mutateAsync({ id, data: payload })
        : await createMutation.mutateAsync({ ...payload, calibration_schedule_id: values.calibration_schedule_id });
    navigate(`/grc/calibration/records/${saved.id}`);
  };

  if (isEdit && loadingExisting) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  const locked = isEdit && existing?.quality_review_status && existing.quality_review_status !== 'PENDING';
  const back = () => navigate(isEdit && id ? `/grc/calibration/records/${id}` : '/grc/calibration');

  return (
    <div style={{ padding: '24px', maxWidth: 1100 }}>
      <Space align="center" style={{ marginBottom: 16 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={back}>
          Back
        </Button>
      </Space>
      <Title level={2} style={{ marginTop: 0 }}>
        {isEdit ? `Edit calibration record ${existing?.calibration_number ?? ''}` : 'Record calibration'}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Capture the result, readings and certificate details. The record is reviewed by Quality against the acceptance
        criteria before the equipment is released for use.
      </Text>

      {locked && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`This record is ${existing?.quality_review_status?.toLowerCase()} and can no longer be edited.`}
        />
      )}
      {result === 'Fail' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="A failed calibration puts the equipment on hold (Not for use) immediately"
          description="Record the failure reason and corrective action. Assess previously measured results for validity."
        />
      )}

      <Form form={form} layout="vertical" onFinish={onFinish} disabled={Boolean(locked)}>
        <Card title="Calibration" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="calibration_schedule_id"
                label="Calibration schedule (equipment)"
                rules={[{ required: true, message: 'Select a schedule' }]}
              >
                <Select
                  showSearch
                  disabled={isEdit}
                  placeholder="Select a calibration schedule"
                  loading={schedulesLoading}
                  options={scheduleOptions}
                  filterOption={(input, option) =>
                    (option?.label as string | undefined)?.toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                />
              </Form.Item>
            </Col>
            {selectedSchedule?.acceptance_criteria && (
              <Col xs={24}>
                <Alert
                  type="info"
                  style={{ marginBottom: 16 }}
                  message="Acceptance criteria"
                  description={selectedSchedule.acceptance_criteria}
                />
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item name="calibration_date" label="Calibration date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="result" label="Result" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'Pass', label: 'Pass' },
                    { value: 'Conditional', label: 'Conditional (limited use)' },
                    { value: 'Fail', label: 'Fail' },
                    { value: 'Pending', label: 'Pending (awaiting certificate)' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="calibration_valid_until"
                label="Valid until"
                extra="Leave blank to use the schedule frequency"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="supplier_id" label="Calibration supplier">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.supplier_code ? `${s.supplier_code} — ${s.supplier_name}` : s.supplier_name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_provider" label="Provider (free text)">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calibrator_name" label="Calibrated by (person)">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="calibration_certificate_number" label="Certificate number">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="traceability_basis" label="Traceability basis">
                <Select allowClear options={TRACEABILITY_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="measurement_uncertainty" label="Measurement uncertainty">
                <Input placeholder="e.g. ±0.005 mm (k=2)" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="environmental_conditions" label="Environmental conditions">
                <Input placeholder="e.g. 20 °C ± 1, 45 % RH" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="adjusted" label="Adjusted during calibration" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Readings"
          style={{ marginBottom: 16 }}
          extra={
            <Button size="small" icon={<PlusOutlined />} onClick={() => setReadings((r) => [...r, blankReading()])}>
              Add test point
            </Button>
          }
        >
          <Table<ReadingRow>
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={readings}
            scroll={{ x: 900 }}
            columns={[
              {
                title: 'Test point',
                width: 140,
                render: (_, r) => <Input value={r.test_point || ''} onChange={(e) => setReading(r.key, { test_point: e.target.value })} />,
              },
              {
                title: 'Nominal',
                width: 110,
                render: (_, r) => (
                  <Input value={r.nominal_value || ''} onChange={(e) => setReading(r.key, { nominal_value: e.target.value })} />
                ),
              },
              {
                title: 'Tolerance',
                width: 110,
                render: (_, r) => <Input value={r.tolerance || ''} onChange={(e) => setReading(r.key, { tolerance: e.target.value })} />,
              },
              {
                title: 'As found',
                width: 110,
                render: (_, r) => <Input value={r.as_found || ''} onChange={(e) => setReading(r.key, { as_found: e.target.value })} />,
              },
              {
                title: 'As left',
                width: 110,
                render: (_, r) => <Input value={r.as_left || ''} onChange={(e) => setReading(r.key, { as_left: e.target.value })} />,
              },
              {
                title: 'Unit',
                width: 80,
                render: (_, r) => <Input value={r.unit || ''} onChange={(e) => setReading(r.key, { unit: e.target.value })} />,
              },
              {
                title: 'Result',
                width: 110,
                render: (_, r) => (
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    value={r.result || undefined}
                    onChange={(v) => setReading(r.key, { result: v ?? null })}
                    options={[
                      { value: 'PASS', label: 'Pass' },
                      { value: 'FAIL', label: 'Fail' },
                    ]}
                  />
                ),
              },
              {
                title: '',
                width: 50,
                render: (_, r) => (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setReadings((rows) => (rows.length > 1 ? rows.filter((x) => x.key !== r.key) : [blankReading()]))}
                  />
                ),
              },
            ]}
          />
          <Row gutter={24} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Form.Item name="as_found_reading" label="As-found summary">
                <Input placeholder="Optional summary when readings are on the certificate" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="as_left_reading" label="As-left summary">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Notes" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            {result === 'Fail' && (
              <>
                <Col xs={24}>
                  <Form.Item name="failure_reason" label="Failure reason" rules={[{ required: true }]}>
                    <TextArea rows={2} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item name="corrective_action" label="Corrective action">
                    <TextArea rows={2} placeholder="Repair, adjust, downgrade or dispose" />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24}>
              <Form.Item name="calibration_notes" label="Notes">
                <TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Space style={{ marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={createMutation.isPending || updateMutation.isPending}
          >
            Save record
          </Button>
          <Button onClick={back} disabled={false}>
            Cancel
          </Button>
        </Space>
        {!isEdit && (
          <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            Attach the calibration certificate on the record page after saving.
          </Text>
        )}
      </Form>
    </div>
  );
};

export default CalibrationRecordFormPage;
